import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import ScreenSecurity from '../utils/screenSecurity';

/** Plays a lesson video from a signed, short-lived URL (HLS when available,
 * falling back to the direct file) with a few deterrents against casual
 * downloading/screen-recording: HLS chunking (no single file to "save as"),
 * a disabled download button, and blocked right-click/Picture-in-Picture.
 * None of this can stop screen recording in a browser — nothing can. */
export default function ProtectedVideoPlayer({ src, isHls, fallbackSrc, title, onExpired, className = '' }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const reportedExpiredRef = useRef(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState('-1');

  // iOS-only: Apple has no way to block screen recording, only detect it —
  // pause and blank the video while a recording/AirPlay mirror is active.
  useEffect(() => {
    let listenerHandle;
    const reactToCapture = ({ isCaptured }) => {
      setIsCaptured(isCaptured);
      if (!isCaptured) return;
      const video = videoRef.current;
      video?.pause();
      // Native iOS fullscreen video playback renders in its own presentation
      // layer entirely outside the webview's normal DOM compositing, so the
      // visibility:hidden below never reaches it while fullscreen is active
      // - recording keeps capturing the real frame until it's forced back
      // into the page first.
      video?.webkitExitFullscreen?.();
    };
    ScreenSecurity.isCaptured().then(reactToCapture);
    ScreenSecurity.addListener('captureStateChanged', reactToCapture).then((handle) => { listenerHandle = handle; });
    return () => { listenerHandle?.remove(); };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    let manifestAbortController;

    reportedExpiredRef.current = false;
    setQualityLevels([]);
    setSelectedQuality('-1');

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      let usingFallback = false;
      let lastFragmentKey = null;
      let fragmentLoads = [];
      let networkRecoveries = 0;
      let mediaRecoveries = 0;
      const hls = new Hls({
        fragLoadingMaxRetry: 2,
        manifestLoadingMaxRetry: 2,
        levelLoadingMaxRetry: 2,
      });

      const switchToFallback = () => {
        if (usingFallback || !fallbackSrc) return;
        usingFallback = true;
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        setQualityLevels([]);
        setSelectedQuality('-1');
        video.src = fallbackSrc;
        video.load();
      };

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const levels = hls.levels.map((level, index) => ({
          value: String(index),
          label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)} kbps`,
        }));
        setQualityLevels(levels);
      });

      hls.on(Hls.Events.FRAG_LOADING, (_event, data) => {
        const fragmentKey = data.frag?.url || `${data.frag?.level}:${data.frag?.sn}`;
        const now = Date.now();
        if (fragmentKey !== lastFragmentKey) {
          lastFragmentKey = fragmentKey;
          fragmentLoads = [];
        }
        fragmentLoads = fragmentLoads.filter((loadedAt) => now - loadedAt < 15000);
        fragmentLoads.push(now);
        if (fragmentLoads.length > 3) switchToFallback();
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && networkRecoveries < 2) {
          networkRecoveries += 1;
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveries < 2) {
          mediaRecoveries += 1;
          hls.recoverMediaError();
        } else {
          switchToFallback();
        }
      });

      hls.loadSource(src);
      hls.attachMedia(video);
      hlsRef.current = hls;
    } else {
      video.src = src;
      if (isHls) {
        // Safari and iOS use native HLS rather than hls.js. Parse the master
        // playlist so native players also get manual rendition choices.
        manifestAbortController = new AbortController();
        fetch(src, { signal: manifestAbortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HLS manifest returned ${response.status}`);
            return response.text();
          })
          .then((manifest) => {
            const lines = manifest.split(/\r?\n/).map((line) => line.trim());
            const levels = [];
            lines.forEach((line, index) => {
              if (!line.startsWith('#EXT-X-STREAM-INF:')) return;
              const resolution = line.match(/RESOLUTION=\d+x(\d+)/i);
              const nextPath = lines.slice(index + 1).find((item) => item && !item.startsWith('#'));
              if (!nextPath) return;
              levels.push({
                value: new URL(nextPath, src).toString(),
                label: resolution ? `${resolution[1]}p` : t('Video quality'),
              });
            });
            setQualityLevels(levels);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') setQualityLevels([]);
          });
      }
    }

    return () => {
      manifestAbortController?.abort();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, isHls, fallbackSrc, t]);

  const handleError = () => {
    if (reportedExpiredRef.current) return;
    reportedExpiredRef.current = true;
    onExpired?.();
  };

  const handleQualityChange = (event) => {
    const value = event.target.value;
    setSelectedQuality(value);
    if (hlsRef.current) {
      // -1 lets hls.js automatically choose a level based on bandwidth;
      // selecting a concrete level switches immediately for user feedback.
      hlsRef.current.currentLevel = Number(value);
      return;
    }

    // Native HLS does not expose a level-selection API. Point it at the
    // selected child playlist (or back at the adaptive master for Auto),
    // preserving the viewer's position and play/pause state.
    const video = videoRef.current;
    if (!video || !isHls) return;
    const resumeAt = video.currentTime;
    const shouldResume = !video.paused;
    video.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(resumeAt)) video.currentTime = Math.min(resumeAt, video.duration || resumeAt);
      if (shouldResume) video.play().catch(() => {});
    }, { once: true });
    video.src = value === '-1' ? src : value;
    video.load();
  };

  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        title={title}
        className="w-full h-full"
        // WKWebView renders <video> on its own hardware-composited layer,
        // which can paint above regular DOM content regardless of z-index —
        // an overlay div alone isn't guaranteed to actually cover it on
        // screen. Hiding the element itself (not just covering it) is the
        // only reliable way to make sure a recording doesn't still capture
        // the real frame underneath.
        style={{ visibility: isCaptured ? 'hidden' : 'visible' }}
        controls
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onError={handleError}
      />
      {!isCaptured && qualityLevels.length > 0 && (
        <div className="absolute right-3 top-3 z-10">
          <label className="sr-only" htmlFor="video-quality-selector">
            {t('Video quality')}
          </label>
          <select
            id="video-quality-selector"
            value={selectedQuality}
            onChange={handleQualityChange}
            className="rounded-md border border-white/30 bg-black/70 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur focus:border-white focus:outline-none"
          >
            <option value="-1">{t('Auto')}</option>
            {qualityLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {isCaptured && (
        <div className="absolute inset-0 bg-black flex items-center justify-center text-center p-6">
          <p className="text-white text-sm sm:text-base font-medium max-w-xs">
            {t('Video paused — screen recording or mirroring detected.')}
          </p>
        </div>
      )}
    </div>
  );
}
